import React, { useState, useEffect, useCallback, useMemo } from "react";
import { MdAccessTime, MdCheck, MdClose, MdDelete, MdHourglassEmpty, MdTrendingUp, MdWork, MdPerson, MdDateRange } from "react-icons/md";
import { supabase } from "../../../../lib/supabaseClient";
import { fetchAllTimeEntries, logTimeEntry, deleteTimeEntry } from "../../../../hooks/useProjects";

const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "SAR", maximumFractionDigits: 0 }).format(n || 0);
const fmtNumber = (n) => new Intl.NumberFormat("en-US", { maximumFractionDigits: 1 }).format(n || 0);

const INPUT = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #e0e0ee", borderRadius: "9px",
  fontSize: "13px", outline: "none", background: "#fff", fontFamily: "inherit", boxSizing: "border-box",
};

const today = () => new Date().toISOString().split("T")[0];
const weekStart = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1);
  return d.toISOString().split("T")[0];
};
const weekEnd = () => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 7);
  return d.toISOString().split("T")[0];
};
const monthStart = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split("T")[0];
};
const monthEnd = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  d.setDate(0);
  return d.toISOString().split("T")[0];
};

export default function LogHoursPanel({ projects = [] }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selProject, setSelProject] = useState("");
  const [hours, setHours] = useState("");
  const [entryDate, setEntryDate] = useState(today());
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Manager filter
  const [selectedEmployee, setSelectedEmployee] = useState("all");
  const [teamMembers, setTeamMembers] = useState([]);

  // Approval workflow
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [approvingId, setApprovingId] = useState(null);

  // Date range for summary
  const [dateRange, setDateRange] = useState("week");

  // Load user
  useEffect(() => {
    supabase.auth.getUser().then(async ({ data: { user } }) => {
      if (!user) return;
      const { data: emp } = await supabase
        .from("employees")
        .select("id, full_name, avatar_url, job_title, hourly_rate, role")
        .eq("id", user.id)
        .single();
      setCurrentUser(emp || { id: user.id, full_name: user.email, hourly_rate: 0 });
      const admin = emp?.role === "admin" || emp?.role === "manager";
      setIsAdmin(admin);

      if (admin) {
        const { data: team } = await supabase
          .from("employees")
          .select("id, full_name, hourly_rate, job_title")
          .eq("status", "Active")
          .order("full_name");
        setTeamMembers(team || []);
      }
    });
  }, []);

  const loadEntries = useCallback(async () => {
    if (!currentUser) return;
    try {
      setLoading(true);
      let filter = {};
      if (isAdmin && selectedEmployee !== "all") {
        filter.employeeId = selectedEmployee;
      } else if (!isAdmin) {
        filter.employeeId = currentUser.id;
      }
      const data = await fetchAllTimeEntries(filter);
      setEntries(data);
    } catch (err) {
      console.error("Error loading entries:", err);
    } finally {
      setLoading(false);
    }
  }, [currentUser, isAdmin, selectedEmployee]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Realtime
  useEffect(() => {
    const ch = supabase.channel("log_hours_panel")
      .on("postgres_changes", { event: "*", schema: "public", table: "project_time_entries" }, loadEntries)
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [loadEntries]);

  const handleLog = async (e) => {
    e.preventDefault();
    if (!selProject || !hours || !currentUser) return;
    setSaving(true);
    try {
      await logTimeEntry({ 
        projectId: selProject, 
        employeeId: currentUser.id, 
        hours, 
        weekStart: entryDate, 
        description 
      });
      setHours(""); 
      setDescription(""); 
      setEntryDate(today());
      await loadEntries();
    } catch (err) { 
      alert("Error: " + err.message); 
    } finally { 
      setSaving(false); 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this time entry?")) return;
    try { 
      await deleteTimeEntry(id); 
      await loadEntries(); 
    } catch (err) { 
      alert(err.message); 
    }
  };

  // Approval handlers
  const handleApprove = async (id) => {
    setApprovingId(id);
    try {
      const { error } = await supabase.from("project_time_entries")
        .update({
          approved: true,
          approved_by: currentUser.id,
          approved_at: new Date().toISOString(),
          rejection_note: null,
        })
        .eq("id", id);
      if (error) throw error;
      await loadEntries();
    } catch (err) { 
      alert(err.message); 
    } finally { 
      setApprovingId(null); 
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectingId) return;
    try {
      const { error } = await supabase.from("project_time_entries")
        .update({ 
          approved: false,
          rejection_note: rejectNote.trim() || "Rejected by manager" 
        })
        .eq("id", rejectingId);
      if (error) throw error;
      setRejectingId(null); 
      setRejectNote("");
      await loadEntries();
    } catch (err) { 
      alert(err.message); 
    }
  };

  // ========== STATISTICS BASED ON SELECTED EMPLOYEE ==========
  const targetUserId = isAdmin && selectedEmployee !== "all" 
    ? selectedEmployee 
    : currentUser?.id;
  
  const targetUser = isAdmin && selectedEmployee !== "all"
    ? teamMembers.find(m => m.id === selectedEmployee)
    : currentUser;

  const userEntries = entries.filter(e => e.employee_id === targetUserId);
  
  const todayStr = today();
  const weekStartStr = weekStart();
  const weekEndStr = weekEnd();
  const monthStartStr = monthStart();
  const monthEndStr = monthEnd();

  const dailyHours = userEntries
    .filter(e => e.week_start === todayStr)
    .reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const dailyCost = dailyHours * (parseFloat(targetUser?.hourly_rate) || 0);

  const weeklyHours = userEntries
    .filter(e => e.week_start >= weekStartStr && e.week_start <= weekEndStr)
    .reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const weeklyCost = weeklyHours * (parseFloat(targetUser?.hourly_rate) || 0);

  const monthlyHours = userEntries
    .filter(e => e.week_start >= monthStartStr && e.week_start <= monthEndStr)
    .reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const monthlyCost = monthlyHours * (parseFloat(targetUser?.hourly_rate) || 0);

  const totalHours = userEntries.reduce((s, e) => s + parseFloat(e.hours || 0), 0);
  const totalCost = totalHours * (parseFloat(targetUser?.hourly_rate) || 0);

  const projectHours = useMemo(() => {
    const map = new Map();
    userEntries.forEach(e => {
      const projectName = e.projects?.title || "Unknown Project";
      const hrs = parseFloat(e.hours || 0);
      const existing = map.get(projectName) || { hours: 0, entries: [] };
      existing.hours += hrs;
      existing.entries.push(e);
      map.set(projectName, existing);
    });
    return Array.from(map.entries())
      .map(([name, data]) => ({ 
        name, 
        hours: data.hours,
        dailyHours: data.entries.filter(e => e.week_start === todayStr).reduce((s, e) => s + parseFloat(e.hours || 0), 0),
        weeklyHours: data.entries.filter(e => e.week_start >= weekStartStr && e.week_start <= weekEndStr).reduce((s, e) => s + parseFloat(e.hours || 0), 0),
        monthlyHours: data.entries.filter(e => e.week_start >= monthStartStr && e.week_start <= monthEndStr).reduce((s, e) => s + parseFloat(e.hours || 0), 0),
      }))
      .sort((a, b) => b.hours - a.hours);
  }, [userEntries, todayStr, weekStartStr, weekEndStr, monthStartStr, monthEndStr]);

  const pendingEntries = isAdmin
    ? entries.filter(e => !e.approved && !e.rejection_note)
    : [];

  const tableEntries = isAdmin ? entries : userEntries;
  const hourlyRate = parseFloat(targetUser?.hourly_rate) || 0;

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: "24px", alignItems: "start" }}>

      {/* LEFT: Employee Summary & Projects Table */}
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

        {/* Employee Selector (Admin only) */}
        {isAdmin && (
          <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e8e8f0", padding: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "12px" }}>
              <MdPerson size={16} color="#3761E9" />
              <span style={{ fontSize: "11px", fontWeight: 700, color: "#aaa", textTransform: "uppercase", letterSpacing: "0.5px" }}>Select Employee</span>
            </div>
            <select 
              value={selectedEmployee} 
              onChange={e => setSelectedEmployee(e.target.value)} 
              style={{ ...INPUT, background: "#fafafa" }}
            >
              <option value="all">📊 All Team (Summary View)</option>
              {teamMembers.map(m => (
                <option key={m.id} value={m.id}>
                  👤 {m.full_name} {m.job_title ? `· ${m.job_title}` : ""} · {m.hourly_rate || 0} SAR/hr
                </option>
              ))}
            </select>
            {selectedEmployee !== "all" && targetUser && (
              <div style={{ marginTop: "10px", padding: "8px 12px", background: "#EEF2FF", borderRadius: "8px" }}>
                <span style={{ fontSize: "11px", color: "#3761E9", fontWeight: 600 }}>
                  Viewing: {targetUser.full_name} · Rate: {hourlyRate} SAR/hr
                </span>
              </div>
            )}
            {selectedEmployee === "all" && (
              <div style={{ marginTop: "10px", padding: "8px 12px", background: "#FFF8E1", borderRadius: "8px" }}>
                <span style={{ fontSize: "11px", color: "#E65100", fontWeight: 600 }}>
                  📊 Showing aggregated data for ALL employees
                </span>
              </div>
            )}
          </div>
        )}

        {/* User Info Card */}
        {(!isAdmin || selectedEmployee !== "all") && targetUser && (
          <div style={{ background: "linear-gradient(135deg, #1a1a2e, #0f3460)", borderRadius: "14px", padding: "18px", color: "#fff" }}>
            <p style={{ fontSize: "10px", color: "rgba(255,255,255,0.5)", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "6px" }}>
              {isAdmin ? "Selected Employee" : "Logged in as"}
            </p>
            <p style={{ fontSize: "15px", fontWeight: 800 }}>{targetUser.full_name}</p>
            <p style={{ fontSize: "11px", color: "rgba(255,255,255,0.55)", marginTop: "2px" }}>
              {targetUser.job_title || "Employee"} · Hourly Rate: <strong>SAR {hourlyRate.toLocaleString()}/hr</strong>
            </p>
            {isAdmin && selectedEmployee !== "all" && (
              <span style={{ marginTop: "8px", display: "inline-block", background: "#3761E9", color: "#fff", fontSize: "10px", fontWeight: 700, padding: "2px 8px", borderRadius: "999px" }}>
                Manager Viewing
              </span>
            )}
          </div>
        )}

        {/* Date Range Selector */}
        <div style={{ display: "flex", gap: "8px", background: "#fff", borderRadius: "12px", padding: "8px", border: "1px solid #e8e8f0" }}>
          {[
            { id: "week", label: "This Week", icon: MdDateRange },
            { id: "month", label: "This Month", icon: MdDateRange },
            { id: "all", label: "All Time", icon: MdTrendingUp },
          ].map(r => {
            const Icon = r.icon;
            return (
              <button
                key={r.id}
                onClick={() => setDateRange(r.id)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: "8px",
                  border: "none",
                  background: dateRange === r.id ? "#3761E9" : "#f5f5f5",
                  color: dateRange === r.id ? "#fff" : "#666",
                  fontSize: "11px",
                  fontWeight: dateRange === r.id ? 700 : 500,
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "5px",
                  fontFamily: "inherit",
                  transition: "all .15s",
                }}
              >
                <Icon size={12} /> {r.label}
              </button>
            );
          })}
        </div>

        {/* Summary Cards */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
          {dateRange === "week" && (
            <>
              <div style={{ background: "#EEF2FF", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#3761E9", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Weekly Hours</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{fmtNumber(weeklyHours)}h</p>
                {weeklyCost > 0 && <p style={{ fontSize: "10px", color: "#3761E9" }}>≈ {fmt(weeklyCost)}</p>}
              </div>
              <div style={{ background: "#E8F5E9", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#2E7D32", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Daily Avg</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{fmtNumber(weeklyHours / 5)}h</p>
                <p style={{ fontSize: "10px", color: "#2E7D32" }}>per work day</p>
              </div>
            </>
          )}
          {dateRange === "month" && (
            <>
              <div style={{ background: "#EEF2FF", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#3761E9", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Monthly Hours</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{fmtNumber(monthlyHours)}h</p>
                {monthlyCost > 0 && <p style={{ fontSize: "10px", color: "#3761E9" }}>≈ {fmt(monthlyCost)}</p>}
              </div>
              <div style={{ background: "#E8F5E9", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#2E7D32", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Daily Avg</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{fmtNumber(monthlyHours / 22)}h</p>
                <p style={{ fontSize: "10px", color: "#2E7D32" }}>per work day</p>
              </div>
            </>
          )}
          {dateRange === "all" && (
            <>
              <div style={{ background: "#EEF2FF", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#3761E9", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Total Hours</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{fmtNumber(totalHours)}h</p>
                {totalCost > 0 && <p style={{ fontSize: "10px", color: "#3761E9" }}>≈ {fmt(totalCost)}</p>}
              </div>
              <div style={{ background: "#E8F5E9", borderRadius: "12px", padding: "14px" }}>
                <p style={{ fontSize: "9px", color: "#2E7D32", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "4px" }}>Projects Worked</p>
                <p style={{ fontSize: "28px", fontWeight: 900, color: "#1a1a2e" }}>{projectHours.length}</p>
                <p style={{ fontSize: "10px", color: "#2E7D32" }}>active projects</p>
              </div>
            </>
          )}
        </div>

        {/* Hours by Project - Table Style */}
        <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e8e8f0", overflow: "hidden" }}>
          <div style={{ padding: "14px 16px", borderBottom: "1px solid #e8e8f0", background: "#f7f8ff" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#1a1a2e", display: "flex", alignItems: "center", gap: "6px" }}>
              <MdWork size={14} /> Projects Hours Breakdown
            </p>
          </div>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {projectHours.length === 0 ? (
              <div style={{ padding: "40px", textAlign: "center", color: "#bbb", fontSize: "12px" }}>
                No hours logged yet
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #f0f0f8", background: "#fff" }}>
                    <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase" }}>Project</th>
                    <th style={{ padding: "10px 8px", textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase" }}>Daily</th>
                    <th style={{ padding: "10px 8px", textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase" }}>Weekly</th>
                    <th style={{ padding: "10px 8px", textAlign: "center", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase" }}>Monthly</th>
                    <th style={{ padding: "10px 12px", textAlign: "right", fontSize: "10px", fontWeight: 700, color: "#aaa", textTransform: "uppercase" }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {projectHours.map((project, idx) => (
                    <tr key={project.name} style={{ borderTop: idx === 0 ? "none" : "1px solid #f5f5f5" }}>
                      <td style={{ padding: "10px 12px", fontWeight: 600, color: "#1a1a2e" }}>{project.name}</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", color: "#3761E9", fontWeight: 600 }}>{fmtNumber(project.dailyHours)}h</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", color: "#FB8C00", fontWeight: 600 }}>{fmtNumber(project.weeklyHours)}h</td>
                      <td style={{ padding: "10px 8px", textAlign: "center", color: "#2E7D32", fontWeight: 600 }}>{fmtNumber(project.monthlyHours)}h</td>
                      <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, color: "#3761E9" }}>{fmtNumber(project.hours)}h</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot style={{ borderTop: "2px solid #e8e8f0", background: "#fafafa" }}>
                  <tr>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#1a1a2e" }}>TOTAL</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#3761E9" }}>{fmtNumber(projectHours.reduce((s, p) => s + p.dailyHours, 0))}h</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#FB8C00" }}>{fmtNumber(projectHours.reduce((s, p) => s + p.weeklyHours, 0))}h</td>
                    <td style={{ padding: "10px 8px", textAlign: "center", fontWeight: 700, color: "#2E7D32" }}>{fmtNumber(projectHours.reduce((s, p) => s + p.monthlyHours, 0))}h</td>
                    <td style={{ padding: "10px 12px", textAlign: "right", fontWeight: 800, color: "#3761E9" }}>{fmtNumber(projectHours.reduce((s, p) => s + p.hours, 0))}h</td>
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT: Form + Approvals + Log Table */}
      <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>

        {/* Log Hours Form */}
        <form onSubmit={handleLog} style={{ background: "#fff", borderRadius: "14px", border: "1.5px solid #3761E9", padding: "20px" }}>
          <p style={{ fontSize: "12px", fontWeight: 700, color: "#3761E9", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: "14px", display: "flex", alignItems: "center", gap: "6px" }}>
            <MdAccessTime size={15} /> Log My Hours
          </p>

          <div style={{ marginBottom: "10px" }}>
            <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", marginBottom: "4px" }}>Project *</label>
            <select value={selProject} onChange={e => setSelProject(e.target.value)} style={INPUT} required>
              <option value="">— Select a project —</option>
              {projects.filter(p => p.status !== "Stopped").map(p => (
                <option key={p.id} value={p.id}>{p.title} {p.clientName ? `· ${p.clientName}` : ""}</option>
              ))}
            </select>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px", marginBottom: "10px" }}>
            <div>
              <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", marginBottom: "4px" }}>Hours *</label>
              <input type="number" step="0.5" min="0.5" value={hours} onChange={e => setHours(e.target.value)} placeholder="e.g. 4" style={INPUT} required />
            </div>
            <div>
              <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", marginBottom: "4px" }}>Date</label>
              <input type="date" value={entryDate} onChange={e => setEntryDate(e.target.value)} style={INPUT} />
            </div>
          </div>

          <div style={{ marginBottom: "12px" }}>
            <label style={{ fontSize: "11px", color: "#666", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.4px", display: "block", marginBottom: "4px" }}>Description</label>
            <input value={description} onChange={e => setDescription(e.target.value)} placeholder="What did you work on?" style={INPUT} />
          </div>

          {hours && hourlyRate > 0 && (
            <div style={{ background: "#f7f9ff", borderRadius: "8px", padding: "8px 12px", marginBottom: "12px", display: "flex", justifyContent: "space-between" }}>
              <span style={{ fontSize: "12px", color: "#666" }}>Cost for {hours}h @ {hourlyRate} SAR/hr</span>
              <span style={{ fontSize: "13px", fontWeight: 700, color: "#3761E9" }}>{fmt(parseFloat(hours) * hourlyRate)}</span>
            </div>
          )}

          <button type="submit" disabled={saving || !selProject || !hours} style={{ width: "100%", padding: "10px", background: "#3761E9", color: "#fff", border: "none", borderRadius: "9px", fontSize: "13px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: (saving || !selProject || !hours) ? 0.6 : 1 }}>
            {saving ? "Saving…" : "✓ Log Hours"}
          </button>
        </form>

        {/* Pending Approvals (Manager only) */}
        {isAdmin && pendingEntries.length > 0 && (
          <div style={{ background: "#fff", borderRadius: "14px", border: "1.5px solid #FB8C00", overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #FFF3E0", background: "#FFFDE7", display: "flex", alignItems: "center", gap: "8px" }}>
              <MdHourglassEmpty size={15} color="#FB8C00" />
              <p style={{ fontSize: "12px", fontWeight: 700, color: "#E65100" }}>Pending Approvals ({pendingEntries.length})</p>
            </div>
            <div style={{ maxHeight: "320px", overflowY: "auto" }}>
              {pendingEntries.map(e => {
                const empRate = parseFloat(e.employees?.hourly_rate) || 0;
                const cost = empRate > 0 ? (parseFloat(e.hours) || 0) * empRate : null;
                const isRejectingThis = rejectingId === e.id;
                return (
                  <div key={e.id} style={{ padding: "12px 16px", borderTop: "1px solid #FFF8E1" }}>
                    <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                      <div style={{ width: "32px", height: "32px", borderRadius: "50%", background: "#FB8C00", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0, overflow: "hidden" }}>
                        {(e.employees?.full_name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: "12px", fontWeight: 700, color: "#E65100" }}>{e.employees?.full_name || "Unknown"}</p>
                        <p style={{ fontSize: "12px", color: "#1a1a2e" }}>{e.projects?.title || "—"}</p>
                        <p style={{ fontSize: "10px", color: "#aaa" }}>{e.week_start} · {e.hours}h {e.description ? `· ${e.description}` : ""} {cost ? `· ${fmt(cost)}` : ""}</p>
                      </div>
                      {!isRejectingThis && (
                        <div style={{ display: "flex", gap: "6px", flexShrink: 0 }}>
                          <button onClick={() => handleApprove(e.id)} disabled={approvingId === e.id} style={{ padding: "5px 10px", background: "#E8F5E9", color: "#2E7D32", border: "1.5px solid #A5D6A7", borderRadius: "8px", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>
                            <MdCheck size={13} /> {approvingId === e.id ? "…" : "Approve"}
                          </button>
                          <button onClick={() => { setRejectingId(e.id); setRejectNote(""); }} style={{ padding: "5px 10px", background: "#FFEBEE", color: "#B71C1C", border: "1.5px solid #EF9A9A", borderRadius: "8px", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>
                            <MdClose size={13} /> Reject
                          </button>
                        </div>
                      )}
                    </div>
                    {isRejectingThis && (
                      <div style={{ marginTop: "10px", padding: "10px", background: "#FFF3E0", borderRadius: "8px", display: "flex", gap: "8px" }}>
                        <input value={rejectNote} onChange={e => setRejectNote(e.target.value)} placeholder="Rejection reason (optional)…" autoFocus style={{ ...INPUT, flex: 1, padding: "7px 10px" }} />
                        <button onClick={handleRejectSubmit} style={{ padding: "7px 12px", background: "#B71C1C", color: "#fff", border: "none", borderRadius: "8px", fontSize: "11px", fontWeight: 700, cursor: "pointer" }}>Confirm</button>
                        <button onClick={() => { setRejectingId(null); setRejectNote(""); }} style={{ padding: "7px 10px", background: "#eee", color: "#666", borderRadius: "8px", fontSize: "11px", cursor: "pointer" }}>Cancel</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Entries Log Table */}
        <div style={{ background: "#fff", borderRadius: "14px", border: "1px solid #e8e8f0", overflow: "hidden" }}>
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f0f8", background: "#f7f8ff" }}>
            <p style={{ fontSize: "12px", fontWeight: 700, color: "#1a1a2e" }}>
              {isAdmin && selectedEmployee !== "all" ? `${targetUser?.full_name || "Employee"}'s Log` : isAdmin ? "All Team Log" : "My Log"} 
              <span style={{ fontWeight: 400, color: "#aaa", fontSize: "11px", marginLeft: "6px" }}>({tableEntries.length} entries)</span>
            </p>
          </div>
          <div style={{ maxHeight: "400px", overflowY: "auto" }}>
            {loading ? (
              <p style={{ textAlign: "center", padding: "30px", color: "#bbb", fontSize: "13px" }}>Loading…</p>
            ) : tableEntries.length === 0 ? (
              <p style={{ textAlign: "center", padding: "30px", color: "#bbb", fontSize: "13px" }}>No entries yet</p>
            ) : (
              tableEntries.map(e => {
                const empRate = parseFloat(e.employees?.hourly_rate) || 0;
                const cost = empRate > 0 ? (parseFloat(e.hours) || 0) * empRate : null;
                const statusBadge = e.approved
                  ? { bg: "#E8F5E9", color: "#2E7D32", label: "✓ Approved" }
                  : e.rejection_note
                    ? { bg: "#FFEBEE", color: "#B71C1C", label: "✗ Rejected" }
                    : { bg: "#FFF8E1", color: "#FB8C00", label: "⏳ Pending" };
                return (
                  <div key={e.id} style={{ display: "flex", gap: "10px", alignItems: "center", padding: "10px 16px", borderTop: "1px solid #f5f5f5" }}
                    onMouseEnter={ex => ex.currentTarget.style.background = "#fafafa"}
                    onMouseLeave={ex => ex.currentTarget.style.background = ""}>
                    <div style={{ width: "30px", height: "30px", borderRadius: "50%", background: "#3761E9", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "10px", fontWeight: 700, flexShrink: 0, overflow: "hidden" }}>
                      {(e.employees?.full_name || "?").split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      {isAdmin && <p style={{ fontSize: "11px", fontWeight: 700, color: "#3761E9", marginBottom: "1px" }}>{e.employees?.full_name || "Unknown"}</p>}
                      <p style={{ fontSize: "12px", fontWeight: 600, color: "#1a1a2e" }}>{e.projects?.title || "—"}</p>
                      <p style={{ fontSize: "10px", color: "#aaa" }}>{e.week_start} · {e.hours}h {e.description ? `· ${e.description}` : ""}</p>
                      {e.rejection_note && <p style={{ fontSize: "10px", color: "#B71C1C", marginTop: "2px" }}>✗ {e.rejection_note}</p>}
                    </div>
                    <span style={{ background: statusBadge.bg, color: statusBadge.color, borderRadius: "999px", padding: "3px 10px", fontSize: "10px", fontWeight: 700, flexShrink: 0, whiteSpace: "nowrap" }}>
                      {statusBadge.label}
                    </span>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      <span style={{ fontSize: "13px", fontWeight: 700, color: cost ? "#2E7D32" : "#aaa" }}>
                        {cost ? fmt(cost) : `${e.hours}h`}
                      </span>
                      {empRate > 0 && <p style={{ fontSize: "9px", color: "#bbb" }}>{empRate} SAR/hr</p>}
                    </div>
                    {(isAdmin || e.employee_id === currentUser?.id) && (
                      <button onClick={() => handleDelete(e.id)} style={{ background: "none", border: "none", cursor: "pointer", color: "#ddd", padding: "4px", flexShrink: 0, borderRadius: "6px" }}
                        onMouseEnter={ex => { ex.currentTarget.style.color = "#E53935"; ex.currentTarget.style.background = "#FFEBEE"; }}
                        onMouseLeave={ex => { ex.currentTarget.style.color = "#ddd"; ex.currentTarget.style.background = "none"; }}>
                        <MdDelete size={14} />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}